/**
 * AddEditItemModal - Modal for adding or editing bucket list items
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, differenceInDays } from "date-fns";
import { Calendar as CalendarIcon, Lock, Globe, Heart, MapPin, Compass, Star, Target, Clock } from "lucide-react";
import LocationPicker from "@/components/LocationPicker";

const CATEGORIES = [
  { value: "travel", label: "Travel", icon: MapPin, color: "primary" },
  { value: "adventures", label: "Adventures", icon: Compass, color: "secondary" },
  { value: "experiences", label: "Experiences", icon: Star, color: "accent" },
  { value: "goals", label: "Goals", icon: Target, color: "charcoal" },
  { value: "romance", label: "Romance", icon: Heart, color: "coral" },
];

const PRIORITIES = [
  { value: "low", label: "Low", color: "secondary" },
  { value: "medium", label: "Medium", color: "accent" },
  { value: "high", label: "High", color: "destructive" },
];

export const AddEditItemModal = ({
  isOpen,
  onClose,
  editItem,
  formData,
  setFormData,
  onSubmit,
  onReset,
  addToGroup,
}) => {
  // Calculate duration when dates change
  const duration = formData.start_date && formData.end_date 
    ? differenceInDays(formData.end_date, formData.start_date) + 1
    : null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-card border-none rounded-2xl p-0 max-h-[85vh] flex flex-col">
        <DialogHeader className="p-6 pb-4 shrink-0">
          <DialogTitle className="font-serif text-2xl text-charcoal">
            {editItem ? "Edit Item" : addToGroup ? `Add to ${addToGroup.name}` : "Add to Bucket List"}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {editItem ? "Update your bucket list item" : addToGroup ? `This item will be shared with the ${addToGroup.name} group` : "What dream do you want to chase together?"}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={onSubmit} className="p-6 pt-0 space-y-6 overflow-y-auto flex-1"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-charcoal font-medium">
              What's the dream?
            </Label>
            <Input
              id="title"
              placeholder="e.g., Visit Paris together"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="h-12 bg-background border-charcoal/10 rounded-xl"
              data-testid="item-title-input"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-charcoal font-medium">
              Description (optional)
            </Label>
            <Textarea
              id="description"
              placeholder="Add some details..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="bg-background border-charcoal/10 rounded-xl resize-none"
              rows={3}
              data-testid="item-description-input"
            />
          </div>

          {/* Category & Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-charcoal font-medium">Category</Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger className="h-12 bg-background border-charcoal/10 rounded-xl" data-testid="item-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      <div className="flex items-center gap-2">
                        <cat.icon className="w-4 h-4" />
                        {cat.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-charcoal font-medium">Priority</Label>
              <Select 
                value={formData.priority} 
                onValueChange={(value) => setFormData({ ...formData, priority: value })}
              >
                <SelectTrigger className="h-12 bg-background border-charcoal/10 rounded-xl" data-testid="item-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map(pri => (
                    <SelectItem key={pri.value} value={pri.value}>{pri.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Trip Duration - Start & End Date */}
          <div className="space-y-2">
            <Label className="text-charcoal font-medium flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Trip Duration (optional)
            </Label>
            <div className="grid grid-cols-2 gap-3">
              {/* Start Date */}
              <Popover modal={true}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full h-11 justify-start text-left font-normal bg-background border-charcoal/10 rounded-xl text-sm"
                    data-testid="item-start-date-input"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                    {formData.start_date ? (
                      format(formData.start_date, "MMM d, yyyy")
                    ) : (
                      <span className="text-muted-foreground">Start date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.start_date}
                    onSelect={(date) => setFormData({ ...formData, start_date: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {/* End Date */}
              <Popover modal={true}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full h-11 justify-start text-left font-normal bg-background border-charcoal/10 rounded-xl text-sm"
                    data-testid="item-end-date-input"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                    {formData.end_date ? (
                      format(formData.end_date, "MMM d, yyyy")
                    ) : (
                      <span className="text-muted-foreground">End date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.end_date}
                    onSelect={(date) => setFormData({ ...formData, end_date: date })}
                    disabled={(date) => formData.start_date && date < formData.start_date}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            {duration && (
              <p className="text-xs text-primary font-medium">
                {duration} {duration === 1 ? 'day' : 'days'} trip
              </p>
            )}
          </div>

          {/* Destination */}
          <div className="space-y-2">
            <Label className="text-charcoal font-medium">Destination (optional)</Label>
            <LocationPicker
              value={{
                location: formData.location,
                country: formData.country,
                country_code: formData.country_code,
                latitude: formData.latitude,
                longitude: formData.longitude,
              }}
              onChange={(locationData) => {
                setFormData({ ...formData, ...locationData });
              }}
            />
            <p className="text-xs text-muted-foreground">
              Add a location to track on your world map
            </p>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes" className="text-charcoal font-medium">
              Notes (optional)
            </Label>
            <Textarea
              id="notes"
              placeholder="Any additional notes..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="bg-background border-charcoal/10 rounded-xl resize-none"
              rows={2}
              data-testid="item-notes-input"
            />
          </div>

          {/* Private Toggle */}
          <div className="flex items-center justify-between p-4 bg-background rounded-xl">
            <div className="flex items-center gap-3">
              {formData.is_private ? (
                <Lock className="w-5 h-5 text-muted-foreground" />
              ) : (
                <Globe className="w-5 h-5 text-secondary" />
              )}
              <div>
                <p className="font-medium text-charcoal text-sm">
                  {formData.is_private ? "Private Item" : "Shared Item"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formData.is_private ? "Only you can see this" : "Visible to your partner"}
                </p>
              </div>
            </div>
            <Checkbox
              checked={formData.is_private}
              onCheckedChange={(checked) => setFormData({ ...formData, is_private: checked })}
              data-testid="item-private-checkbox"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onClose(false);
                onReset();
              }}
              className="flex-1 h-12 rounded-xl border-charcoal/10"
              data-testid="cancel-item-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 h-12 bg-primary text-white hover:bg-primary/90 rounded-xl btn-press"
              data-testid="save-item-btn"
            >
              {editItem ? "Save Changes" : "Add to List"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditItemModal;
